import {
  fetchVapidPublicKey,
  registerDeviceToken,
  unregisterDeviceToken,
  type AuthState,
} from '../api/client';

/**
 * Web Push — dukungan sama seperti CRM (`apps/web/src/lib/webPush.ts`), tetapi kunci VAPID DAN
 * langganannya MILIK WORKFORCE SENDIRI. Keduanya terikat origin peramban yang mendaftar
 * (`workforce.saleshubpro.tech`), jadi tidak ada yang bisa dibagi antar keduanya.
 */
export type PushState = 'unsupported' | 'unconfigured' | 'denied' | 'off' | 'on';

function didukung(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/**
 * `updateViaCache: 'none'` supaya pengecekan pembaruan berkala peramban mengambil `/sw.js`
 * langsung dari jaringan, bukan dari cache HTTP — tanpa ini perbaikan pada service worker bisa
 * butuh waktu lama untuk sampai ke peramban yang sudah pernah membukanya.
 */
async function registrasi(): Promise<ServiceWorkerRegistration> {
  const reg = await navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' });
  await navigator.serviceWorker.ready;
  return reg;
}

/** Dipanggil sekali saat aplikasi dimuat — bukan hanya saat mengaktifkan push. */
export async function segarkanServiceWorker(): Promise<void> {
  if (!didukung()) return;
  try {
    const reg = await navigator.serviceWorker.getRegistration('/sw.js');
    await reg?.update();
  } catch {
    // Terbaik-upaya — kegagalan di sini tidak boleh mengganggu boot aplikasi.
  }
}

function keyBytes(base64url: string): Uint8Array {
  const padding = '='.repeat((4 - (base64url.length % 4)) % 4);
  const base64 = (base64url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export async function getPushState(auth: AuthState): Promise<PushState> {
  if (!didukung()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';

  const { publicKey } = await fetchVapidPublicKey(auth);
  if (!publicKey) return 'unconfigured';

  const reg = await navigator.serviceWorker.getRegistration('/sw.js');
  const sub = await reg?.pushManager.getSubscription();
  return sub ? 'on' : 'off';
}

/** Wajib dipanggil dari gestur pengguna (klik) — `Notification.requestPermission()` menuntutnya. */
export async function enablePush(auth: AuthState): Promise<void> {
  if (!didukung()) {
    throw new Error('Peramban ini belum mendukung notifikasi.');
  }
  const { publicKey } = await fetchVapidPublicKey(auth);
  if (!publicKey) {
    throw new Error('Notifikasi perangkat belum dikonfigurasi di server.');
  }
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Izin notifikasi tidak diberikan.');
  }
  const reg = await registrasi();
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: keyBytes(publicKey) as BufferSource,
    }));
  await registerDeviceToken(auth, {
    token: JSON.stringify(sub.toJSON()),
    platform: 'web',
    deviceName: navigator.userAgent.slice(0, 120),
  });
}

export async function disablePush(auth: AuthState): Promise<void> {
  const reg = await navigator.serviceWorker.getRegistration('/sw.js');
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return;
  const token = JSON.stringify(sub.toJSON());
  await sub.unsubscribe();
  await unregisterDeviceToken(auth, { token }).catch(() => {
    // Langganan di peramban sudah dicabut — baris di server akan dibersihkan otomatis saat
    // provider push melaporkannya mati pada kiriman berikutnya (lihat `WebPushService`).
  });
}
